/* FormulaAuditLog — admin страница: пълна история на NIS/DIL формулите.
 *
 * Compliance: EU AI Act Article 13 — transparency obligations.
 * Кой ползва тази страница:
 *   • Admin / compliance officer — за регулаторни одити (CE-mark, MDR)
 *   • Clinicians — за разбиране защо коеф. X е тази стойност
 *   • Researchers / peer reviewers — за reproducibility audit
 *
 * Backend: GET /api/admin/formula-audit-log (admin-only)
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Loader2, History, ShieldCheck, ExternalLink, Star } from "lucide-react";

export default function FormulaAuditLog() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all | NIS | DIL

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/formula-audit-log`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  const entries = useMemo(() => {
    if (!data?.entries) return [];
    return filter === "all" ? data.entries : data.entries.filter((e) => e.formula === filter);
  }, [data, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="formula-audit-log-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${isDark ? "bg-red-950/50 text-red-300" : "bg-red-50 text-red-700"}`}
           data-testid="formula-audit-log-error">
        {bg ? "Грешка при зареждане:" : "Failed to load:"} {error}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="formula-audit-log">
      {/* Header */}
      <div className={`p-5 rounded-2xl border ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)]" : "border-slate-200 bg-white"}`}>
        <div className="flex items-start gap-3 mb-3">
          <div className={`p-2.5 rounded-xl ${isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
            <History className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Одит на формулите — NIS & DIL" : "Formula Audit Log — NIS & DIL"}
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg
                ? "Пълна история на промените на клиничните алгоритми. EU AI Act Article 13 — transparency obligations."
                : "Full history of clinical-algorithm changes. EU AI Act Article 13 — transparency obligations."}
            </p>
          </div>
        </div>

        {/* Current versions banner */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          {["nis", "dil"].map((k) => (
            <div key={k}
              className={`flex items-center justify-between p-3 rounded-xl border ${
                isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,12%)]" : "border-slate-200 bg-slate-50"
              }`}>
              <div>
                <div className={`text-[10px] uppercase font-bold tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {bg ? "Активна" : "Active"} {k.toUpperCase()}
                </div>
                <div className={`text-2xl font-bold mt-0.5 ${isDark ? "text-white" : "text-slate-900"}`}>
                  {data?.current?.[k] || "—"}
                </div>
              </div>
              <ShieldCheck className={`w-6 h-6 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mt-4">
          <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "Филтър:" : "Filter:"}
          </span>
          {["all", "NIS", "DIL"].map((k) => (
            <button key={k}
              onClick={() => setFilter(k)}
              data-testid={`formula-audit-filter-${k.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === k
                  ? (isDark ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-emerald-100 text-emerald-700 border border-emerald-300")
                  : (isDark ? "bg-slate-800/50 text-slate-400 hover:bg-slate-800 border border-transparent" : "bg-slate-50 text-slate-600 hover:bg-slate-100 border border-transparent")
              }`}>
              {k === "all" ? (bg ? "Всички" : "All") : k}
            </button>
          ))}
          <span className={`ml-auto text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>
            {entries.length} {bg ? "записа" : "entries"}
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className={`absolute left-[18px] top-0 bottom-0 w-px ${isDark ? "bg-slate-700" : "bg-slate-200"}`}></div>

        <div className="space-y-3">
          {entries.map((e, i) => <TimelineEntry key={`${e.formula}-${e.version}-${i}`} entry={e} isDark={isDark} bg={bg} />)}
        </div>
      </div>
    </div>
  );
}

function TimelineEntry({ entry, isDark, bg }) {
  const isFrozen = !!entry.frozen;
  const isCurrent = (entry.frozen_text || "").includes("Currently active");
  const color = entry.formula === "NIS" ? "emerald" : "amber";
  const colorCls = entry.formula === "NIS"
    ? (isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-emerald-100 text-emerald-700 border-emerald-300")
    : (isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-amber-100 text-amber-700 border-amber-300");

  return (
    <div className="relative pl-12" data-testid={`formula-audit-entry-${entry.formula}-${entry.version}`}>
      {/* Dot on timeline */}
      <div className={`absolute left-[12px] top-3 w-[15px] h-[15px] rounded-full border-2 ${
        isCurrent
          ? "border-emerald-500 bg-emerald-500 animate-pulse"
          : isFrozen
            ? (isDark ? "border-slate-600 bg-slate-700" : "border-slate-300 bg-white")
            : (isDark ? "border-amber-500 bg-amber-600" : "border-amber-400 bg-amber-100")
      }`}></div>

      <div className={`p-4 rounded-xl border ${isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)]" : "border-slate-200 bg-white"} ${
        isCurrent ? (isDark ? "ring-1 ring-emerald-500/40" : "ring-1 ring-emerald-300") : ""
      }`}>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${colorCls}`}>
            {entry.formula}
          </span>
          <span className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {entry.version}
          </span>
          <span className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            · {entry.date}
          </span>
          {entry.reviewer_round !== null && entry.reviewer_round !== undefined && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
              isDark ? "bg-purple-500/20 text-purple-300" : "bg-purple-100 text-purple-700"
            }`}>
              {bg ? `Реценз. кр. ${entry.reviewer_round}` : `Reviewer R${entry.reviewer_round}`}
            </span>
          )}
          {isCurrent && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${isDark ? "text-emerald-300" : "text-emerald-600"}`}>
              <Star className="w-3 h-3 fill-current" />
              {bg ? "Активна" : "Active"}
            </span>
          )}
        </div>

        {entry.frozen_text && (
          <div className={`text-[11px] mb-2 italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {entry.frozen_text}
          </div>
        )}

        {/* Coefficients */}
        {entry.coefficients && Object.keys(entry.coefficients).length > 0 && (
          <div className={`mb-2 p-2 rounded font-mono text-[10px] ${
            isDark ? "bg-slate-900/50 text-slate-300" : "bg-slate-50 text-slate-700"
          }`}>
            {Object.entries(entry.coefficients).map(([k, v]) => (
              <div key={k}>
                <span className={isDark ? "text-slate-500" : "text-slate-400"}>{k}:</span>{" "}
                <span className="font-bold">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Changes */}
        <ul className={`text-xs space-y-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
          {entry.changes.map((c, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className={isDark ? "text-slate-600" : "text-slate-300"}>•</span>
              <span dangerouslySetInnerHTML={{
                __html: c.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"),
              }} />
            </li>
          ))}
        </ul>

        {/* PMIDs */}
        {entry.pmids && entry.pmids.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <span className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>PMID:</span>
            {entry.pmids.map((pmid) => (
              <a key={pmid}
                href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`}
                target="_blank" rel="noopener noreferrer"
                className={`inline-flex items-center gap-0.5 text-[10px] font-mono ${
                  isDark ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"
                } hover:underline`}>
                {pmid}<ExternalLink className="w-2.5 h-2.5" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
